import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Format date to dd/mm/yyyy format
 * @param {string|Date} dateInput - Date string or Date object
 * @param {object} options - Formatting options
 * @param {boolean} options.includeTime - Include time in output (HH:MM)
 * @param {boolean} options.includeWeekday - Include weekday name
 * @param {boolean} options.shortMonth - Use short month name instead of numeric
 * @returns {string} Formatted date string
 */
export function formatDate(dateInput, options = {}) {
  if (!dateInput) return '-';
  
  try {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    
    if (isNaN(date.getTime())) return '-';
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    
    let result = `${day}/${month}/${year}`;
    
    if (options.shortMonth) {
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      result = `${day} ${monthNames[date.getMonth()]} ${year}`;
    }
    
    if (options.includeWeekday) {
      const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      result = `${weekdays[date.getDay()]}, ${result}`;
    }
    
    if (options.includeTime) {
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      result += ` ${hours}:${minutes}`;
    }
    
    return result;
  } catch (error) {
    console.error('Date formatting error:', error);
    return '-';
  }
}

/**
 * Format date for display in date range (short format)
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {string} Formatted as "DD Mon" (e.g., "15 Jan")
 */
export function formatDateShort(dateInput) {
  if (!dateInput) return '-';
  
  try {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    if (isNaN(date.getTime())) return '-';
    
    const day = String(date.getDate()).padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${day} ${monthNames[date.getMonth()]}`;
  } catch (error) {
    return '-';
  }
}

/**
 * Format datetime with full details
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {string} Formatted as "DD/MM/YYYY HH:MM"
 */
export function formatDateTime(dateInput) {
  return formatDate(dateInput, { includeTime: true });
}

/**
 * Format item category for display
 * Standardizes category names to: FNG, RMT, SFG, CTM
 * @param {string} category - Raw category from backend
 * @returns {string} Standardized category display name
 */
export function formatCategory(category) {
  if (!category) return '-';
  
  const normalized = category.toLowerCase().trim();
  
  const categoryMap = {
    'raw_material': 'RMT',
    'rmt': 'RMT',
    'finished_goods': 'FNG',
    'finished_good': 'FNG',
    'fng': 'FNG',
    'semi_finished': 'SFG',
    'sfg': 'SFG',
    'consumable': 'CTM',
    'ctm': 'CTM',
    'wip': 'WIP'
  };
  
  return categoryMap[normalized] || category.toUpperCase();
}
